"use client";

import Button from "@marginly/ui/components/button";
import Badge from "@marginly/ui/components/label";
import Thumbnail from "@marginly/ui/components/thumbnail";
import T from "@marginly/ui/components/typography";
import React from "react";
import { useAccount } from "wagmi";

import { CONTEST_ID } from "@/components/raffle/constants";
import { useGetScore } from "@/components/raffle/hooks";
import { MAIN_APP_URL, POOLS_URL } from "@/constants/links";
import { useMemberInfo } from "@/contexts/contest-member-info/hooks";
import {
  trackRaffleLendBannerClick,
  trackRaffleLendClick,
  trackRaffleTradeClick,
  trackShareResultClick,
} from "@/util/tracking";

import { Container } from "../common/container.styled";
import Footer from "../common/footer/footer";
import Space from "../common/space";
import QaSection from "../qa-section";
import { ReferralLink } from "../raffle/referral-link";
import { RegistrationButton } from "../raffle/registration-button";
import { useRegistration } from "../raffle/registration-button/use-registration";
import { RaffleResults } from "../raffle/results/raffle-results";
import { Noop } from "../util";
import {
  Banner,
  LabelWrapper,
  Prize,
  PrizesGrid,
  Task,
  TasksBlock,
  TasksSubgrid,
  ThumbnailsRow,
} from "./raffle.styled";

const makeGetScoreByType = (score: Score[]) => (type: Achievement) =>
  score.find((item) => item.type === type)?.score ?? 0;

const RaffleMain = () => {
  const { refCode } = useMemberInfo();
  const { registration } = useRegistration();
  const { address } = useAccount();
  const score = useGetScore(CONTEST_ID, address);
  const getScoreByType = makeGetScoreByType(score);
  const referrerScore = getScoreByType("Referrer");
  const socialPostScore = getScoreByType("SocialPost");
  const tradeScore = getScoreByType("Trade");
  const liquidityScore = getScoreByType("LiquidityProvider");

  return (
    <div>
      <Space height={192} heightMobile={96} />
      <Container>
        <section>
          <div style={{ textAlign: "center" }}>
            <T headerL>Complete tasks and get tickets</T>
            <Space height={12} heightMobile={6} />
            <T body secondary>
              Earned tickets are eligible for all subsequent raffles unless
              stated otherwise.
            </T>
          </div>
          <Space height={48} heightMobile={48} />

          <TasksBlock>
            <Task className="friends">
              <img src="/images/raffle-flower.png" alt="" className="img" />

              {referrerScore ? (
                <LabelWrapper>
                  <Badge md>
                    <img src="/images/ticket-black.svg" alt="" />
                    {referrerScore}
                  </Badge>
                </LabelWrapper>
              ) : (
                <Noop />
              )}
              <div className="container">
                <div>
                  <T headerS>Refer friends</T>
                  <Space height={2} heightMobile={2} />
                  <T body secondary>
                    3 tickets for each friend who makes a $100+ trade.
                  </T>
                </div>
                <Space height={10} heightMobile={0} />
                {refCode ? (
                  <ReferralLink referralCode={refCode} />
                ) : (
                  <Button secondary md className="btn" onClick={registration}>
                    <T action>Get Referral Link</T>
                  </Button>
                )}
              </div>
            </Task>
            <TasksSubgrid>
              <Task>
                <img src="/images/task-phone.png" className="phone" alt="" />
                <img
                  src="/images/task-phone-2.png"
                  className="phone-mobile"
                  alt=""
                />

                {socialPostScore ? (
                  <LabelWrapper>
                    <Badge md>
                      <img src="/images/ticket-black.svg" alt="" />
                      {socialPostScore}
                    </Badge>
                  </LabelWrapper>
                ) : (
                  <Noop />
                )}
                <div className="container">
                  <div>
                    <T headerS>Post trade result</T>
                    <Space height={2} heightMobile={2} />
                    <T body secondary>
                      2 tickets per post up to 6 tickets per week.
                    </T>
                    <Space height={32} heightMobile={60} />
                    <ThumbnailsRow>
                      <Thumbnail md className="ticket">
                        <img src="./images/ticket.svg" alt="" />
                      </Thumbnail>
                      <Thumbnail md className="ticket">
                        <img src="./images/ticket.svg" alt="" />
                      </Thumbnail>
                    </ThumbnailsRow>
                    <Space height={32} heightMobile={60} />
                  </div>
                  <a href={MAIN_APP_URL} onClick={trackShareResultClick}>
                    <Button secondary md className="btn">
                      <T action>Share Result</T>
                    </Button>
                  </a>
                </div>
              </Task>

              <div className="column">
                <Task>
                  <img src="/images/ticket2.png" className="icon" alt="" />

                  {tradeScore ? (
                    <LabelWrapper>
                      <Badge md>
                        <img src="/images/ticket-black.svg" alt="" />
                        {tradeScore}
                      </Badge>
                    </LabelWrapper>
                  ) : (
                    <Noop />
                  )}
                  <Thumbnail md className="ticket ticket-absolute">
                    <img src="./images/ticket.svg" alt="" />
                  </Thumbnail>
                  <div className="container">
                    <div>
                      <T headerS>Generate trade volume</T>
                      <Space height={2} heightMobile={2} />
                      <T body secondary>
                        1 ticket per $100.
                      </T>
                    </div>
                    <a
                      href={MAIN_APP_URL}
                      onClick={() => trackRaffleTradeClick()}
                    >
                      <Button secondary md className="btn">
                        <T action>Trade</T>
                      </Button>
                    </a>
                  </div>
                </Task>
                <Task>
                  <img src="/images/ticket1.png" className="icon" alt="" />

                  {liquidityScore ? (
                    <LabelWrapper>
                      <Badge md>
                        <img src="/images/ticket-black.svg" alt="" />
                        {liquidityScore}
                      </Badge>
                    </LabelWrapper>
                  ) : (
                    <Noop />
                  )}
                  <Thumbnail md className="ticket ticket-absolute">
                    <img src="./images/ticket.svg" alt="" />
                  </Thumbnail>
                  <div className="container">
                    <div>
                      <T headerS>Provide liquidity</T>
                      <Space height={2} heightMobile={2} />
                      <T body secondary>
                        1 ticket per $100.
                      </T>
                    </div>
                    <a href={POOLS_URL} onClick={() => trackRaffleLendClick()}>
                      <Button secondary md className="btn">
                        <T action>Lend</T>
                      </Button>
                    </a>
                  </div>
                </Task>
              </div>
            </TasksSubgrid>
          </TasksBlock>
          {/* <Space height={48} heightMobile={48} />
          <Banner>
            <img src="/images/flash-g.svg" alt="" className="icon" />
            <T body>
              Get 3x ticket boost by depositing liquidity to any pool by Dec 31
            </T>
            <a href={POOLS_URL} onClick={() => trackRaffleLendBannerClick()}>
              <Button sm elevated className="btn">
                <T action>Lend</T>
              </Button>
            </a>
          </Banner> */}
        </section>

        <Space height={192} heightMobile={96} />

        <RaffleResults />

        <Space height={192} heightMobile={96} />

        <section>
          <div style={{ textAlign: "center" }}>
            <T headerL>$10K worth of prizes</T>
            <Space height={12} heightMobile={12} />
            <T body secondary>
              Given away in 4 weekly raffles. The more tickets you have the
              higher your chances to win!
            </T>
          </div>
          <Space height={48} heightMobile={48} />

          <PrizesGrid>
            <Prize>
              <img src="./images/prize-1000.png" alt="" />
              <T headerS>$1,000</T>
              <T body secondary>
                Four lucky tickets win the Jackpot.
              </T>
            </Prize>
            <Prize>
              <img src="./images/prize-50.png" alt="" />
              <T headerS>$50</T>
              <T body secondary>
                120 random tickets win $50 prizes.
              </T>
            </Prize>
          </PrizesGrid>
          <Space height={48} heightMobile={32} />
          <span style={{ display: "flex", justifyContent: "center" }}>
            <RegistrationButton />
          </span>
        </section>
        <Space height={192} heightMobile={96} />
        <QaSection />
        <Space height={192} heightMobile={96} />
      </Container>
      <Footer />
    </div>
  );
};

export default RaffleMain;
